import { Badge } from 'flowbite-react'
import { Event, RoundDetails } from '@/types'
import { TimeCountdown } from '@/components/common'
import { isAfter, isBefore } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { formatDate } from '@/utils/formatDate'
import { JoinHackathonCTA } from './JoinHackathonCTA'

interface CountdownSectionProps {
  label: string
  endDate: number
  currentDate: number
}

const CountdownSection = ({
  label,
  endDate,
  currentDate,
}: CountdownSectionProps) => (
  <div className="flex items-center justify-start gap-3 my-6">
    {label}
    <TimeCountdown
      defaultTime={Math.floor(
        (new Date(endDate).getTime() - currentDate) / 1000
      )}
    />
  </div>
)

interface EventActionsProps {
  event: Event
  currentEventUrl: string
  userId: string | undefined
  eventRound: RoundDetails | undefined
  selectTab: (value: number) => void
}

export const EventActions = ({
  event,
  currentEventUrl,
  userId,
  selectTab,
}: EventActionsProps) => {
  const currentDate = new Date(
    toZonedTime(new Date(), event.zoneinfo)
  ).getTime()

  const formattedRegistrationStartDate = new Date(
    formatDate(
      event.registration_start_date,
      'dd MMM yyyy, HH:mm',
      event.zoneinfo
    )
  ).getTime()
  const formattedRegistrationEndDate = new Date(
    formatDate(
      event.registration_end_date,
      'dd MMM yyyy, HH:mm',
      event.zoneinfo
    )
  ).getTime()

  const formattedSubmissionStartDate = event.submission_start_date
    ? new Date(
        formatDate(
          event.submission_start_date,
          'dd MMM yyyy, HH:mm',
          event.zoneinfo
        )
      ).getTime()
    : null
  const formattedSubmissionEndDate = event.submission_end_date
    ? new Date(
        formatDate(
          event.submission_end_date,
          'dd MMM yyyy, HH:mm',
          event.zoneinfo
        )
      ).getTime()
    : null

  const CTA = () => (
    <JoinHackathonCTA
      event={event}
      currentEventUrl={currentEventUrl}
      userId={userId}
      selectTab={selectTab}
    />
  )

  const Upcoming = () => (
    <>
      <CountdownSection
        label="Starting in"
        endDate={formattedRegistrationStartDate}
        currentDate={currentDate}
      />
      <Thumbnail />
      <CTA />
    </>
  )

  const RegisterNow = () => {
    const showCountdown =
      (formattedSubmissionStartDate &&
        isBefore(currentDate, formattedSubmissionStartDate)) ||
      (formattedRegistrationEndDate &&
        !isAfter(currentDate, formattedRegistrationEndDate) &&
        !event.has_submission_form)
    return (
      <>
        {showCountdown && (
          <CountdownSection
            label="Registrations close in"
            endDate={formattedRegistrationEndDate}
            currentDate={currentDate}
          />
        )}
        <Thumbnail />
        <CTA />
      </>
    )
  }

  const Ongoing = () => (
    <>
      {formattedSubmissionStartDate &&
        isBefore(currentDate, formattedSubmissionStartDate) &&
        isAfter(currentDate, formattedRegistrationEndDate) && (
          <CountdownSection
            label="Submission start in"
            endDate={formattedSubmissionStartDate}
            currentDate={currentDate}
          />
        )}
      {formattedSubmissionEndDate &&
        !isAfter(currentDate, formattedSubmissionEndDate) &&
        formattedSubmissionStartDate &&
        !isBefore(currentDate, formattedSubmissionStartDate) && (
          <CountdownSection
            label={
              !isAfter(currentDate, formattedRegistrationEndDate)
                ? 'Registrations close in'
                : 'Submissions end in'
            }
            endDate={
              !isAfter(currentDate, formattedRegistrationEndDate)
                ? formattedRegistrationEndDate
                : formattedSubmissionEndDate
            }
            currentDate={currentDate}
          />
        )}
      <Thumbnail />
      <CTA />
    </>
  )

  const Closed = () => (
    <div className="flex flex-col">
      <div className="mt-auto">
        <CTA />
      </div>
    </div>
  )

  // What the desktop sidebar carries, for a phone where there is no sidebar.
  // The thumbnail itself is left out on purpose: the banner above it already
  // shows the same artwork, so repeating it only pushed the page down.
  const Thumbnail = () => (
    <div className="lg:hidden">
      <div className="text-gray-800">{event.description}</div>
      <div className="flex flex-wrap gap-2 mt-4">
        {event.tag_list &&
          event.tag_list.map((tag, index) => (
            <Badge
              key={index}
              className="px-2.5 py-0.5 bg-sky-100 text-neutral-500 rounded-md"
            >
              <span className="text-blue-800 text-xs font-medium">{tag}</span>
            </Badge>
          ))}
      </div>
    </div>
  )

  return (
    <div className="w-full flex flex-col gap-x-3 gap-y-4 mt-4">
      {event.state === 'upcoming' && <Upcoming />}
      {event.state === 'register_now' && <RegisterNow />}
      {event.state === 'ongoing' && <Ongoing />}
      {event.state === 'closed' && <Closed />}
    </div>
  )
}
